import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import {
  filter,
  forEach,
  map,
  uniq,
  cloneDeep,
  difference,
  sortBy,
  remove,
  find,
  get,
  minBy,
} from "lodash";
import { MultiselectWithHelpersComponent } from "../multiselect-with-helpers/multiselect-with-helpers.component";
import { school } from "../../store/schools/state";
import { MatTableDataSource } from "@angular/material/table";

@Component({
  selector: "visibility-settings-table",
  templateUrl: "./visibility-settings-table.component.html",
  styleUrls: ["./visibility-settings-table.component.scss"],
})
export class VisibilitySettingsTableComponent
  extends MultiselectWithHelpersComponent
  implements OnInit {
  @Input() metrics;
  @Input() schools: Array<school>;

  columns: Array<string>;
  bindLabel: string = "officialSchoolName";
  groupBy: string = "schoolLevel";
  placeholder: string = "Select schools";
  helperCategories: Array<string>;
  dataSource;
  systemSchoolIds: Array<string>;
  numItemsShown: number = 4;

  constructor() {
    super();
  }

  ngOnInit() { }

  ngOnChanges(changes) {
    this.helperCategories = sortBy(
      uniq(map(this.schools, this.groupBy)),
      (item) => {
        return parseInt(
          get(
            minBy(filter(this.schools, [this.groupBy, item]), "minGradeLevel"),
            "minGradeLevel"
          )
        );
      }
    );
    this.columns = [
      "long_name",
      "visible",
      "all",
      ...this.helperCategories,
      "multi_schools",
    ];

    // This check is needed because "metrics" can trigger this change before "schools", and in 
    // that scenario, the loop removes all the systemSchoolIds from each metric. This persists
    // even when the schools trigger this again, because they've already been filtered out.
    if (this.schools) {
      this.schools = this.orderItems(this.schools);
      this.systemSchoolIds = map(this.schools, "systemSchoolId");
      forEach(this.metrics, (metric) => {
        // remove any systemSchoolIds which are not present in schools.json
        metric.systemSchoolIds = filter(
          metric.systemSchoolIds,
          (systemSchoolId: string) => {
            return !!find(this.schools, ["systemSchoolId", systemSchoolId]);
          }
        );
        // check which categories are pre-selected for each item
        if (metric.systemSchoolIds && metric.systemSchoolIds.length > 0) {
          this.updateAccelerators(metric);
        }
      });
    }
    this.dataSource = new MatTableDataSource(this.metrics);
  }

  checkSelectedSome(items, selectedItems) {
    return (
      !items.every((ai) => selectedItems.includes(ai)) &&
      items.some((ai) => selectedItems.includes(ai))
    );
  }

  updateAccelerators(metric) {
    metric.selectedAll = this.checkSelectedAll(
      this.systemSchoolIds,
      metric.systemSchoolIds
    );
    metric.selectedSome = this.checkSelectedSome(
      this.systemSchoolIds,
      metric.systemSchoolIds
    ); //for indeterminate state
    // check for each level
    this.updateHelperCategoryAccelerators(metric);
  }

  updateHelperCategoryAccelerators(metric) {
    forEach(this.helperCategories, (cat) => {
      metric["selectedAll" + cat] = this.checkSelectedAll(
        map(filter(this.schools, ["schoolLevel", cat]), "systemSchoolId"),
        metric.systemSchoolIds
      );
      if (!metric["selectedAll" + cat]) {
        metric["selectedSome" + cat] = this.checkSelectedSome(
          map(filter(this.schools, ["schoolLevel", cat]), "systemSchoolId"),
          metric.systemSchoolIds
        );
      } else {
        metric["selectedSome" + cat] = false;
      }
    });
  }

  selectAllChecked(metric) {
    if (metric.selectedAll) {
      metric.systemSchoolIds = cloneDeep(this.systemSchoolIds);
    } else {
      metric.systemSchoolIds = [];
    }
    metric.selectedSome = false;
    this.updateHelperCategoryAccelerators(metric);
  }

  helperCategoryChecked(metric, category) {
    if (metric["selectedAll" + category]) {
      // add any schools from this category to the array of systemSchoolIds for this metric
      metric.systemSchoolIds = [
        ...metric.systemSchoolIds,
        ...map(
          filter(this.schools, ["schoolLevel", category]),
          "systemSchoolId"
        ),
      ];
    } else {
      // remove any schools from this category from the array of systemSchoolIds
      metric.systemSchoolIds = difference(
        metric.systemSchoolIds,
        map(filter(this.schools, ["schoolLevel", category]), "systemSchoolId")
      );
    }
    metric["selectedSome" + category] = false;
    metric.selectedAll = this.checkSelectedAll(
      this.systemSchoolIds,
      metric.systemSchoolIds
    );
    metric.selectedSome = this.checkSelectedSome(
      this.systemSchoolIds,
      metric.systemSchoolIds
    ); //for indeterminate state
  }

  visibilityToggled(metric) {
    metric.visible = !metric.visible;
  }

  clearAll(metric) {
    metric.systemSchoolIds = [];
    metric.selectedAll = false;
    metric.selectedSome = false;
    this.updateHelperCategoryAccelerators(metric);
  }

  clearSchool(school, metric) {
    metric.systemSchoolIds = remove(
      metric.systemSchoolIds,
      (id) => id != school.systemSchoolId
    );
    metric.selectedAll = false;
    metric.selectedSome = this.checkSelectedSome(
      this.systemSchoolIds,
      metric.systemSchoolIds
    ); //for indeterminate state
    this.updateHelperCategoryAccelerators(metric);
  }
}
